import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Formik, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { UserRole } from '../../types';
import { FaPhone, FaLock, FaChalkboardTeacher } from 'react-icons/fa';

const TeacherLogin: React.FC = () => {
  const { signIn } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const validationSchema = Yup.object({
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10,15}$/, 'رقم الهاتف غير صالح')
      .required('رقم الهاتف مطلوب'),
    password: Yup.string()
      .required('كلمة المرور مطلوبة')
  });

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6}>
          <Card className="auth-card">
            <Card.Header className="bg-primary text-white text-center py-4">
              <h2 className="mb-0">تسجيل دخول المعلم</h2>
            </Card.Header>
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <div className="auth-icon-container">
                  <FaChalkboardTeacher size={40} className="auth-icon" />
                </div>
                <h4 className="mt-3">مرحبًا بك في منصة المعلمين</h4>
                <p className="text-muted">قم بتسجيل الدخول للوصول إلى لوحة تحكم المعلم</p>
              </div>
              
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Formik
                initialValues={{
                  phoneNumber: '',
                  password: ''
                }}
                validationSchema={validationSchema}
                onSubmit={async (values, { setSubmitting }) => {
                  try {
                    setError(null);
                    setLoading(true);

                    // تحويل رقم الهاتف إلى صيغة البريد الإلكتروني
                    const email = `${values.phoneNumber}@example.com`;
                    
                    await signIn(email, values.password);
                    
                    // التحقق من صلاحيات المستخدم (يجب أن يكون معلم)
                    const userRole = localStorage.getItem('userRole');
                    
                    if (userRole === UserRole.TEACHER) {
                      navigate('/teacher/dashboard');
                    } else {
                      setError('ليس لديك صلاحيات للدخول كمعلم');
                      // تسجيل الخروج
                      // await signOut();
                    }
                  } catch (err: any) {
                    console.error('Login error:', err);
                    setError('خطأ في تسجيل الدخول. تأكد من رقم الهاتف وكلمة المرور');
                  } finally {
                    setLoading(false);
                    setSubmitting(false);
                  }
                }}
              >
                {({
                  handleSubmit,
                  isSubmitting
                }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4" controlId="phoneNumber">
                      <Form.Label className="fw-bold">رقم الهاتف</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaPhone className="text-primary" />
                        </span>
                        <Field
                          as={Form.Control}
                          type="text"
                          name="phoneNumber"
                          placeholder="أدخل رقم الهاتف"
                          className="py-2"
                        />
                      </div>
                      <ErrorMessage
                        name="phoneNumber"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="password">
                      <Form.Label className="fw-bold">كلمة المرور</Form.Label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaLock className="text-primary" />
                        </span>
                        <Field
                          as={Form.Control}
                          type="password"
                          name="password"
                          placeholder="أدخل كلمة المرور"
                          className="py-2"
                        />
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-danger mt-1"
                      />
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 py-2 mt-3 mb-3 d-flex align-items-center justify-content-center"
                      disabled={loading || isSubmitting}
                    >
                      {loading ? (
                        <>جاري تسجيل الدخول...</>
                      ) : (
                        <>
                          <FaChalkboardTeacher className="me-2" />
                          تسجيل الدخول
                        </>
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>
              
              <div className="text-center mt-4 pt-3 border-top">
                <p className="mb-0">
                  <Link to="/" className="text-primary fw-bold text-decoration-none">العودة إلى الصفحة الرئيسية</Link>
                </p>
              </div>
            </Card.Body>
            <Card.Footer className="bg-light text-center py-3">
              <p className="text-muted mb-0"> {new Date().getFullYear()} منصة السامي التعليمية. جميع الحقوق محفوظة</p>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TeacherLogin;
